/*
 * Project: Dynamedics Portal Web
 * Created Date: Tuesday December 5th 2023
 * Author: KasunKarunasekara
 * -----
 * Last Modified: Tuesday December 5th 2023 12:29:09 am
 * Modified By: KasunKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2023 Mitra Sparks
 * -----
 * HISTORY:
 */

import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';

// ----------------------------------------------------------------------

const ListItemStyle = styled(ListItem)(({ theme }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  return {
    ...theme.typography.body2,
    height: '100%',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    color: theme.palette.common.white,
    borderBottom: !isMobile && `5px solid transparent`,
    '&.isActiveRoot': {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: alpha(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity
      ),
      '&:before': {
        top: 0,
        right: 0,
        bottom: 0,
        width: 5,
        content: "''",
        position: 'absolute',
        backgroundColor: isMobile && '#ED6C05'
      },
      borderBottom: !isMobile && `5px solid #ED6C05`
    },
    '&.isActiveSub': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      '& .subIcon:before': {
        transform: 'scale(2)',
        backgroundColor: theme.palette.primary.main
      }
    },
    minWidth: 80,
    display: 'flex',
    justifyContent: isMobile ? 'left' : 'center',
    width: 'auto'
  };
});

const SubIconStyle = styled('span')(({ theme }) => ({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:before': {
    width: 4,
    height: 4,
    content: "''",
    display: 'block',
    borderRadius: '50%',
    backgroundColor: theme.palette.text.disabled,
    transition: theme.transitions.create('transform')
  }
}));

// ----------------------------------------------------------------------

NavbarComponent.propTypes = {
  level: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  info: PropTypes.element,
  icon: PropTypes.element,
  open: PropTypes.bool,
  children: PropTypes.node,
  isMobile: PropTypes.bool
};

export default function NavbarComponent({
  level,
  title,
  href,
  info,
  icon,
  open = false,
  children,
  isMobile = true
}) {
  const theme = useTheme();
  const [show, setShow] = useState(open);
  const isSubItem = level > 0;
  const [active, setActive] = useState(false);
  const handleShow = () => {
    setShow((show) => !show);
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          button
          disableGutters
          onClick={handleShow}
          className={open ? 'isActiveRoot' : ''}
        >
          {/* <ListItemIcon>{icon}</ListItemIcon> */}
          <ListItemText
            disableTypography
            primary={title}
            sx={{ paddingBottom: '15px' }}
          />
          {info}
          <Box
            component={Icon}
            icon={show ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16 }}
          />
        </ListItemStyle>

        <Collapse in={show}>{children}</Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      button
      to={href}
      exact={open}
      disableGutters
      component={RouterLink}
      activeClassName={isSubItem ? 'isActiveSub' : 'isActiveRoot'}
      isActive={(match, location) => {
        if (!match) {
          return false;
        }

        const { url } = match;
        const { pathname } = location;
        const isMatch = url === pathname;

        if (!isSubItem) {
          return url.length && pathname.includes(url);
        }

        return isMatch;
      }}
    >
      <Box sx={{ pt: '22px' }}>
        <Box sx={{ display: 'flex' }}>
          {/* <ListItemIcon>{icon ? icon : <></>}</ListItemIcon> */}
          <ListItemText
            disableTypography
            primary={title}
            sx={{ pb: '10px', textAign: 'center' }}
          />
        </Box>
        {/* {active && (
          <Box sx={{ height: 5, bgcolor: theme.palette.common.white }}></Box>
        )} */}
      </Box>
      {info}
    </ListItemStyle>
  );
}
